import React, { useCallback, useEffect } from 'react';
import { compose } from 'redux';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Button, Spin, message } from 'antd';
import * as HttpStatus from 'http-status-codes';
import Modal from '../../../../../components/Modal/Modal';
import ModuleEmptyState from '../../../../../components/ModuleEmptyState';
import OrganisationMainSection from '../../../../../components/OrganisationMainSection/OrganisationMainSection';
import Table from '../../../../../components/Table/Table';
import PlanExpiryBadge from '../../../../../components/PlanExpiryBadge/PlanExpiryBadge';
import PlanEdit, { PLAN_ACTIONS } from './PlanEdit';
import PlanDeleteConfirm from './PlanDeleteConfirm';
import {
  createPlan,
  clonePlan,
  updatePlan,
  deletePlan,
  renewPlan,
  fetchPlanSubscriptions,
  fetchPlans,
  fetchAllPlans,
} from '../PlanAction';
import { fetchBranch } from '../../../../Organisation/scenes/Branch/BranchAction';
import { fetchContact } from '../../../../Organisation/scenes/Contact/ContactAction';
import { selectOrganisation } from '../../../../Organisation/OrganisationAction';
import { isPurchaseEnabled } from '../../../../../utils/featureGates';
import {
  loadingPlansSelector,
  loadingPlanSubscriptionsSelector,
  currenciesSelector,
  getExpiryDate,
  isExpiryBeforeToday,
  isPlanAccessRevoked,
  isPromoBackedPlan,
  planSubscriptionsSelector,
} from '../PlanSelectors';
import { MSG_ERROR, MSG_PAYMENT_REQUIRED } from '../../../../../constants';
import { getApiErrorMessage, parseHttpError } from '../../../../../utils/httpError';
import explanations from '../data/explanations.json';

const PLAN_EMPTY_HELP = (
  <>
    <p>
      <strong>{explanations.create.title}</strong>: {explanations.create.text}
    </p>
    <p>
      <strong>{explanations.subscriptionKey.title}</strong>: {explanations.subscriptionKey.text}
    </p>
  </>
);

/** Extra steps content shown in Organisation “Plans” page tooltip beside full governance copy */
export const PLAN_APPENDIX_DETAIL = PLAN_EMPTY_HELP;

const PLAN_EMPTY_TIP =
  'Tip: Subscription keys unlock new plans. Purchase additional keys anytime via Buy subscriptions.';

const PROMO_SUBSCRIPTION_EXPIRED_MESSAGE =
  'Your promo code provider does not have a valid subscription for this plan. Please contact your promo code provider and ask them to activate the plan.';

const EXPIRED_POLICY_MESSAGE =
  'Your policy has expired. Please renew your subscription to continue using this plan.';

const PlanList = ({
  history,
  orgId,
  planList = [],
  loadingPlans,
  loadingPlanSubscriptions,
  fetchBranch,
  fetchContact,
  createPlan,
  updatePlan,
  deletePlan,
  renewPlan,
  selectOrganisation,
  currencies,
  fetchPlanSubscriptions,
  fetchPlans,
  fetchAllPlans,
  planSubscriptions,
  /** Narrow chrome for Admin collapse / nested contexts */
  embedded = false,
  sectionTooltip = null,
  sectionGuidance = null,
}) => {
  useEffect(() => {
    fetchBranch(orgId);
    fetchContact(orgId);
  }, [orgId, fetchBranch, fetchContact]);

  useEffect(() => {
    planList.forEach((plan) => {
      if (
        !planSubscriptions ||
        (planSubscriptions && !Object.prototype.hasOwnProperty.call(planSubscriptions, plan.planId))
      ) {
        fetchPlanSubscriptions(plan.planId);
      }
    });
  }, [planList, planSubscriptions, fetchPlanSubscriptions]);

  const refreshPlans = useCallback(async () => {
    try {
      if (embedded) await fetchAllPlans();
      else if (orgId) await fetchPlans(orgId);
      else await fetchPlans();
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  }, [embedded, fetchAllPlans, fetchPlans, orgId]);

  const expiryLabel = (plan) => getExpiryDate(plan);

  const blockedMessageFor = (plan) => {
    if (!isExpiryBeforeToday(expiryLabel(plan))) return null;
    if (isPromoBackedPlan(plan)) return PROMO_SUBSCRIPTION_EXPIRED_MESSAGE;
    return EXPIRED_POLICY_MESSAGE;
  };

  const goToPlan = async (plan) => {
    const blockedMessage = blockedMessageFor(plan);
    if (blockedMessage) {
      message.error(blockedMessage);
      return;
    }

    try {
      await selectOrganisation(plan.organisationId, { plan });
      history.push('/dashboard');
    } catch {
      message.error('Failed to switch plan');
    }
  };

  const onCreate = async (data) => {
    try {
      await createPlan(data);
      // createPlan refreshes org-scoped plans; Admin embedded list uses allPlans.
      if (embedded) await fetchAllPlans();
      message.success('The plan has been successfully created');
    } catch (err) {
      const { status } = parseHttpError(err);
      if (status === HttpStatus.PAYMENT_REQUIRED) message.error(MSG_PAYMENT_REQUIRED);
      else message.error(getApiErrorMessage(err, MSG_ERROR));
      throw err;
    }
  };

  const onUpdate = async (planId, data) => {
    try {
      await updatePlan(planId, data);
      if (embedded) await fetchAllPlans();
      message.success('The plan information has been successfully updated');
    } catch (err) {
      const { status } = parseHttpError(err);
      if (status === HttpStatus.PAYMENT_REQUIRED) message.error(MSG_PAYMENT_REQUIRED);
      else message.error(getApiErrorMessage(err, MSG_ERROR));
      throw err;
    }
  };

  const onDelete = async (planId) => {
    try {
      await deletePlan(planId);
      if (embedded) await fetchAllPlans();
      message.success('The plan has been successfully deleted');
    } catch (err) {
      const { status } = parseHttpError(err);
      if (status === HttpStatus.PAYMENT_REQUIRED) message.error(MSG_PAYMENT_REQUIRED);
      else message.error(getApiErrorMessage(err, MSG_ERROR));
      throw err;
    }
  };

  const onRenew = async (planId, data) => {
    try {
      await renewPlan(planId, data);
      if (embedded) await fetchAllPlans();
      message.success('The plan has been successfully renewed');
    } catch (err) {
      const { status } = parseHttpError(err);
      if (status === HttpStatus.PAYMENT_REQUIRED) message.error(MSG_PAYMENT_REQUIRED);
      else message.error(getApiErrorMessage(err, MSG_ERROR));
      throw err;
    }
    try {
      await fetchPlanSubscriptions(planId);
    } catch {
      message.warning('Plan list updated but subscription dates could not be refreshed. Reload the page if needed.');
    }
  };

  const columns = [
    { key: 'name', title: 'Name', dataIndex: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
    { key: 'branch', title: 'Branch', dataIndex: 'branch' },
    {
      key: 'expiryDate',
      title: 'Expiry Date',
      render: (_text, record) => {
        const exp = expiryLabel(record);
        return exp ? <PlanExpiryBadge expiry={exp} compact /> : '—';
      },
    },
    {
      key: 'accessRevoked',
      title: 'Access revoked ?',
      render: (_text, record) => {
        const subscriptions = Array.isArray(record?.subscriptions) ? record.subscriptions : [];
        if (!subscriptions.length) return '—';
        if (!isPlanAccessRevoked(record)) return 'No';
        return <span className="plan-access-revoked">Yes</span>;
      },
    },
    {
      key: 'currency',
      title: 'Currency',
      render: (text, record) => {
        const currency = _.find(currencies, { symbol: record.currency });
        return <span>{currency?.name}</span>;
      },
    },
    {
      key: 'action',
      width: 340,
      className: 'plan-row-actions-cell',
      render: (text, record) => {
        const showRenew = isExpiryBeforeToday(expiryLabel(record)) && !isPromoBackedPlan(record);

        return (
          <div className="btn-container inline plan-row-actions">
          {showRenew ? (
            <Modal toggle={(show) => <Button type="primary" onClick={show}>Renew</Button>}>
              {(hide) => <PlanEdit plan={record} action={PLAN_ACTIONS.RENEW} renewPlan={onRenew} close={hide} />}
            </Modal>
          ) : (
            <Button type="primary" onClick={() => goToPlan(record)}>
              Go
            </Button>
          )}
          <Modal
            explanation={{ title: explanations.rename.title, text: explanations.rename.text }}
            toggle={(show) => <Button onClick={show}>Edit</Button>}
          >
            {(hide, setExplanation) => (
              <PlanEdit
                plan={record}
                action={PLAN_ACTIONS.EDIT}
                updatePlan={onUpdate}
                close={hide}
                onExplanationChange={setExplanation}
              />
            )}
          </Modal>
          <Modal
            title="Are you absolutely sure?"
            toggle={(show) => (
              <Button type="danger" onClick={show}>
                Delete
              </Button>
            )}
          >
            {(hide) => <PlanDeleteConfirm plan={record} deletePlan={onDelete} close={hide} />}
          </Modal>
          </div>
        );
      },
    },
  ];

  const hasPlans = Array.isArray(planList) && planList.length > 0;
  const createPlanExplanation = {
    title: explanations.create.title,
    text: explanations.create.text,
  };

  const buySubscriptionsToolbar = isPurchaseEnabled() ? (
    <Link to="/purchase">
      <Button type="default" className="organisation-main-section__toolbar-ghost" htmlType="button">
        Buy subscriptions
      </Button>
    </Link>
  ) : null;

  const refreshToolbar = (
    <Button
      type="default"
      className="organisation-main-section__toolbar-ghost"
      htmlType="button"
      icon="reload"
      loading={loadingPlans}
      onClick={refreshPlans}
      aria-label="Refresh plans"
      title="Refresh plans"
    >
      Refresh
    </Button>
  );

  const spinning = loadingPlans || loadingPlanSubscriptions;

  const addNewButton = (label) => (
    <Modal
      explanation={createPlanExplanation}
      toggle={(show) => (
        <Button
          type="primary"
          className="organisation-main-section__toolbar-primary"
          htmlType="button"
          onClick={show}
        >
          {label}
        </Button>
      )}
    >
      {(hide, setExplanation) => (
        <PlanEdit
          action={PLAN_ACTIONS.CREATE}
          createPlan={onCreate}
          close={hide}
          onExplanationChange={setExplanation}
        />
      )}
    </Modal>
  );

  const toolbarActions = (
    <>
      {buySubscriptionsToolbar}
      {refreshToolbar}
      {hasPlans ? addNewButton('Add new') : addNewButton('Add First Plan')}
    </>
  );

  const embeddedBody = !hasPlans ? (
    <ModuleEmptyState
      moduleTitle="Plans"
      embedded
      headline="No plans yet"
      description="Create your first continuity plan and assign it to a business location. Each plan consumes a subscription key from your organisation."
      tip={PLAN_EMPTY_TIP}
      iconType="file-text"
    >
      <Modal
        explanation={{ title: explanations.create.title, text: explanations.create.text }}
        toggle={(show) => (
          <Button type="primary" className="module-empty-state__btn-primary" htmlType="button" onClick={show}>
            Add First Plan
          </Button>
        )}
      >
        {(hide, setExplanation) => (
          <PlanEdit
            action={PLAN_ACTIONS.CREATE}
            createPlan={onCreate}
            close={hide}
            onExplanationChange={setExplanation}
          />
        )}
      </Modal>
      {isPurchaseEnabled() ? (
        <Link to="/purchase">
          <Button className="module-empty-state__btn-secondary" htmlType="button">
            Buy subscriptions
          </Button>
        </Link>
      ) : null}
    </ModuleEmptyState>
  ) : (
    <Table title="Plans" columns={columns} dataSource={planList} rowKey="planId" searchFields={['name', 'branch']} />
  );

  return (
    <OrganisationMainSection
      embedded={embedded}
      title="Plans"
      tooltipContent={sectionTooltip}
      guidance={sectionGuidance}
      toolbarActions={toolbarActions}
    >
      <Spin spinning={spinning}>{embeddedBody}</Spin>
    </OrganisationMainSection>
  );
};

const mapStateToProps = (state) => ({
  loadingPlans: loadingPlansSelector(state),
  currencies: currenciesSelector(state),
  planSubscriptions: planSubscriptionsSelector(state),
  loadingPlanSubscriptions: loadingPlanSubscriptionsSelector(state),
});

const mapDispatchToProps = {
  createPlan,
  clonePlan,
  updatePlan,
  deletePlan,
  renewPlan,
  fetchBranch,
  fetchContact,
  selectOrganisation,
  fetchPlanSubscriptions,
  fetchPlans,
  fetchAllPlans,
};

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(PlanList);
